import { useEffect, useState } from 'react';
import { IconSearch, IconChevronLeft, IconChevronRight, IconAlertCircle, IconReceipt2 } from '@tabler/icons-react';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/ui/Modal';

/**
 * Phase 4.5 — Admin order browser.
 *
 * Filtered + paginated list of every order in the branch, click for
 * detail drawer. MANAGER+ via /api/orders/admin.
 */

interface ApiOrderRow {
  id: string;
  orderNumber: number;
  status: string;
  channel: 'DINE_IN' | 'TAKEAWAY' | 'DELIVERY';
  placedAt: string;
  totalCents: number;
  discountCents: number;
  tipCents: number;
  customerName: string | null;
  cashier: { id: string; name: string };
  payments: { method: string; amountCents: number; refundedAmountCents: number }[];
  _count: { lines: number };
}

interface OrderDetail {
  id: string;
  orderNumber: number;
  status: string;
  channel: string;
  placedAt: string;
  totalCents: number;
  vatCents: number;
  subtotalCents: number;
  discountCents: number;
  tipCents: number;
  customerName: string | null;
  customerPhone: string | null;
  customerEmail: string | null;
  cancelReason: string | null;
  voidReason: string | null;
  cashier: { id: string; name: string };
  lines: Array<{
    id: string;
    lineNo: number;
    name: string;
    variantSize: string | null;
    qty: number;
    unitPriceCents: number;
    lineTotalCents: number;
    voidedAt: string | null;
    voidReason: string | null;
  }>;
  payments: Array<{
    id: string;
    method: string;
    amountCents: number;
    changeCents: number;
    refundedAmountCents: number;
    reference: string | null;
    approvalCode: string | null;
    last4: string | null;
  }>;
}

const STATUSES = ['PLACED', 'PREPARING', 'READY', 'SERVED', 'CANCELLED', 'VOIDED', 'REFUNDED', 'PARTIALLY_REFUNDED', 'PENDING'];

function fmtR(cents: number) {
  return `R ${(cents / 100).toFixed(2)}`;
}

function methodLabel(m: string) {
  return m === 'CARD_YOCO' ? 'Card' :
         m === 'CASH' ? 'Cash' :
         m === 'EFT' ? 'EFT' :
         m === 'ACCOUNT' ? 'Account' :
         m === 'VOUCHER' ? 'Voucher' :
         m === 'GIFT_CARD' ? 'Gift card' :
         'Other';
}

const STATUS_TONE: Record<string, string> = {
  PLACED:             'bg-info-soft text-info',
  PREPARING:          'bg-warning-soft text-warning',
  READY:              'bg-success-soft text-success',
  SERVED:             'bg-success-soft text-success',
  CANCELLED:          'bg-light text-gray-700',
  VOIDED:             'bg-danger-soft text-danger',
  REFUNDED:           'bg-danger-soft text-danger',
  PARTIALLY_REFUNDED: 'bg-warning-soft text-warning',
  PENDING:            'bg-light text-gray-700',
};

export default function AdminOrdersPage() {
  const [orders, setOrders] = useState<ApiOrderRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Detail drawer
  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      if (search.trim()) params.set('q', search.trim());
      if (statusFilter) params.set('status', statusFilter);
      if (fromDate) params.set('from', new Date(fromDate).toISOString());
      if (toDate) {
        const end = new Date(toDate);
        end.setHours(23, 59, 59, 999);
        params.set('to', end.toISOString());
      }
      const r = await fetch(`/api/orders/admin?${params.toString()}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setOrders(data.orders ?? []);
      setTotal(data.total ?? 0);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [page, statusFilter, fromDate, toDate]);

  async function openDetail(id: string) {
    setDetailLoading(true);
    try {
      const r = await fetch(`/api/orders/${id}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setDetail(data.order);
    } finally {
      setDetailLoading(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <AdminLayout title="Orders" subtitle={`${total} total · page ${page} of ${totalPages}`}>
      {error && (
        <div className="rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2 mb-5">
          <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-danger">{error}</p>
        </div>
      )}

      {/* Filter bar */}
      <section className="rounded-lg bg-surface border border-border p-4 mb-4">
        <form
          onSubmit={(e) => { e.preventDefault(); setPage(1); void load(); }}
          className="flex flex-wrap items-end gap-3"
        >
          <div className="flex-1 min-w-[200px]">
            <label className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Search</label>
            <div className="relative">
              <IconSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Order number or customer"
                className="w-full h-10 pl-9 pr-3 rounded-md bg-light border border-border focus:border-primary outline-none text-13 font-semibold text-heading"
              />
            </div>
          </div>
          <div>
            <label className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="h-10 px-3 rounded-md bg-light border border-border focus:border-primary outline-none text-13 font-semibold text-heading"
            >
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
              className="h-10 px-3 rounded-md bg-light border border-border focus:border-primary outline-none text-13 font-semibold text-heading"
            />
          </div>
          <div>
            <label className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); setPage(1); }}
              className="h-10 px-3 rounded-md bg-light border border-border focus:border-primary outline-none text-13 font-semibold text-heading"
            />
          </div>
          <button
            type="submit"
            className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-13 font-bold hover:bg-primary-hover"
          >
            <IconSearch size={14} stroke={2.5} />
            Search
          </button>
        </form>
      </section>

      {/* Results table */}
      <section className="rounded-lg bg-surface border border-border p-4">
        {loading ? (
          <p className="text-13 italic text-gray-700">Loading…</p>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 rounded-2xl bg-light text-gray-700 inline-flex items-center justify-center mb-3">
              <IconReceipt2 size={22} stroke={2} />
            </div>
            <p className="text-14 font-bold text-heading">No orders match</p>
            <p className="text-13 font-medium text-gray-700 mt-1">Try widening the filters or extending the date range.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-13">
              <thead>
                <tr className="text-left text-12 font-bold uppercase tracking-wider text-gray-700">
                  <th className="py-2 pr-3">#</th>
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Customer</th>
                  <th className="py-2 pr-3">Cashier</th>
                  <th className="py-2 pr-3">Payment</th>
                  <th className="py-2 pr-3 text-right">Total</th>
                  <th className="py-2 pr-3 text-right">Lines</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr
                    key={o.id}
                    onClick={() => void openDetail(o.id)}
                    className="border-t border-border-soft cursor-pointer hover:bg-light"
                  >
                    <td className="py-2.5 pr-3 font-bold text-heading tabular-nums">#{o.orderNumber}</td>
                    <td className="py-2.5 pr-3 text-gray-700 tabular-nums">{new Date(o.placedAt).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })}</td>
                    <td className="py-2.5 pr-3">
                      <span className={`inline-flex items-center px-2 h-6 rounded-md text-11 font-bold uppercase tracking-wider ${STATUS_TONE[o.status] ?? 'bg-light text-gray-700'}`}>
                        {o.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3 text-heading">{o.customerName ?? '—'}</td>
                    <td className="py-2.5 pr-3 text-gray-700">{o.cashier.name}</td>
                    <td className="py-2.5 pr-3 text-gray-700">{o.payments.map((p) => methodLabel(p.method)).join(' + ') || '—'}</td>
                    <td className="py-2.5 pr-3 text-right font-bold text-heading tabular-nums">{fmtR(o.totalCents)}</td>
                    <td className="py-2.5 pr-3 text-right text-gray-700 tabular-nums">{o._count.lines}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-border">
          <p className="text-12 font-medium text-gray-700">
            Showing {orders.length === 0 ? 0 : (page - 1) * pageSize + 1}–{(page - 1) * pageSize + orders.length} of {total}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-light border border-border text-13 font-bold text-heading hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <IconChevronLeft size={14} stroke={2.5} />
              Prev
            </button>
            <span className="text-13 font-semibold text-heading tabular-nums">Page {page} / {totalPages}</span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
              className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-light border border-border text-13 font-bold text-heading hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
              <IconChevronRight size={14} stroke={2.5} />
            </button>
          </div>
        </div>
      </section>

      {/* Detail drawer */}
      <Modal open={detail !== null} onClose={() => setDetail(null)} size="xl" title={detail ? `Order #${detail.orderNumber} · ${detail.status}` : 'Loading…'}>
        {detailLoading && <p className="text-13 italic text-gray-700">Loading…</p>}
        {detail && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Tile label="Total" value={fmtR(detail.totalCents)} bold />
              <Tile label="VAT (incl)" value={fmtR(detail.vatCents)} />
              <Tile label="Discount" value={fmtR(detail.discountCents)} />
              <Tile label="Tip" value={fmtR(detail.tipCents)} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-13">
              <Tile label="Placed" value={new Date(detail.placedAt).toLocaleString('en-ZA')} />
              <Tile label="Cashier" value={detail.cashier.name} />
              <Tile label="Channel" value={detail.channel.replace(/_/g, ' ')} />
              <Tile label="Customer" value={detail.customerName ?? '—'} sub={detail.customerPhone ?? detail.customerEmail ?? undefined} />
            </div>
            {detail.cancelReason && <NoteBlock label="Cancel reason" body={detail.cancelReason} tone="warning" />}
            {detail.voidReason && <NoteBlock label="Void reason" body={detail.voidReason} tone="danger" />}

            {/* Lines */}
            <section>
              <h3 className="text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">Items</h3>
              <ul className="space-y-2">
                {detail.lines.map((l) => (
                  <li key={l.id} className={[
                    'flex items-center gap-3 p-3 rounded-md border',
                    l.voidedAt ? 'bg-light border-danger/30 line-through text-gray-600' : 'bg-light border-border-soft',
                  ].join(' ')}>
                    <span className="w-8 h-8 rounded-md bg-primary-soft text-primary inline-flex items-center justify-center text-13 font-bold tabular-nums">{l.qty}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-14 font-bold text-heading truncate">{l.name}{l.variantSize && <span className="ml-2 text-gray-700 font-normal">· {l.variantSize}</span>}</p>
                      {l.voidedAt && l.voidReason && <p className="text-12 font-medium text-danger">Voided: {l.voidReason}</p>}
                    </div>
                    <p className="text-14 font-bold text-heading tabular-nums">{fmtR(l.lineTotalCents)}</p>
                  </li>
                ))}
              </ul>
            </section>

            {/* Payments */}
            <section>
              <h3 className="text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">Payments</h3>
              <ul className="space-y-2">
                {detail.payments.map((p) => (
                  <li key={p.id} className="flex items-center gap-3 p-3 rounded-md bg-light border border-border-soft">
                    <div className="flex-1 min-w-0">
                      <p className="text-14 font-bold text-heading">{methodLabel(p.method)} · {fmtR(p.amountCents)}</p>
                      <p className="text-12 font-medium text-gray-700">
                        {p.changeCents > 0 && <>Change {fmtR(p.changeCents)} · </>}
                        {p.refundedAmountCents > 0 && <>Refunded {fmtR(p.refundedAmountCents)} · </>}
                        {p.last4 && <>****{p.last4} · </>}
                        {p.approvalCode && <>Approval {p.approvalCode} · </>}
                        {p.reference && <>Ref {p.reference}</>}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
}

function Tile({ label, value, sub, bold }: { label: string; value: string; sub?: string; bold?: boolean }) {
  return (
    <div className="rounded-md bg-light border border-border-soft p-3">
      <p className="text-11 font-bold uppercase tracking-wider text-gray-700">{label}</p>
      <p className={`tabular-nums ${bold ? 'text-18 font-bold text-heading' : 'text-15 font-semibold text-heading'} mt-0.5`}>{value}</p>
      {sub && <p className="text-12 font-medium text-gray-700 mt-0.5">{sub}</p>}
    </div>
  );
}

function NoteBlock({ label, body, tone }: { label: string; body: string; tone: 'warning' | 'danger' }) {
  const cls = tone === 'warning' ? 'bg-warning-soft border-warning/30 text-warning' : 'bg-danger-soft border-danger/30 text-danger';
  return (
    <div className={`rounded-md border p-3 ${cls}`}>
      <p className="text-12 font-bold uppercase tracking-wider">{label}</p>
      <p className="text-13 font-semibold mt-1">{body}</p>
    </div>
  );
}
